import { formatNumber } from "$root/localization/formatters";
import { AgGridTooltipWrapper } from "$root/third-party-integrations/ag-grid/ag-grid";
import { Icon, Text } from "@mdotm/shadcn/legacy/components";
import { readAtom, type RwAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { CellClassParams, ColDef, ColGroupDef, ValueFormatterParams } from "ag-grid-community";
import type { CustomTooltipProps } from "ag-grid-react";
import Color from "color";
import { renderToStaticMarkup } from "react-dom/server";
import { canTriggerUpdateComposition, generateColumnColor } from "../../common";
import { currencyCode } from "$root/utils/currency";
import type { PortfolioLabInvestmentItem } from "../../ctx";
import { hasEditableAum, isMonetaryCellEditable, isWeightCellEditable } from "./editability";
import { LabAutoGroupHeader } from "./cell/LabAutoGroupHeader";
import { LabGroupCellRenderer } from "./cell/LabGroupCellRenderer";
import { LabTotalsMonetaryCellRenderer } from "./cell/LabTotalsMonetaryCellRenderer";
import { LabTotalsWeightCellRenderer } from "./cell/LabTotalsWeightCellRenderer";
import { LabDeltaCellRenderer, LabMonetaryCellRenderer, LabWeightCellRenderer } from "./cell/LabValueCellRenderer";
import type { CompositionRow, MergedCompositionRow } from "./rows";
import { LabPortfolioGroupHeader } from "./cell/LabPortfolioGroupHeader";
import { LabNormalizeAndAumCellRenderer } from "./cell/LabNormalizeAndAumCellRenderer";

const MONETARY_AUM_ZERO_MESSAGE = "Please provide a valid AUM in order to edit the monetary value";

/**
 * Tooltip for the monetary (€) column. AG Grid only renders it when `tooltipValueGetter`
 * returns a non-empty value, which we do exclusively for cells disabled because `aum === 0`.
 */
function MonetaryAumTooltip(props: CustomTooltipProps<MergedCompositionRow>): React.JSX.Element | null {
	if (props.value == null || props.value === "") {
		return null;
	}
	return (
		<AgGridTooltipWrapper>
			<Text type="Body/M/Book">{props.value as string}</Text>
		</AgGridTooltipWrapper>
	);
}

export const compositionAutoGroupColumnDef: ColDef<CompositionRow> = {
	headerName: "Name",
	pinned: "left",
	minWidth: 240,
	cellRendererParams: {
		suppressCount: true,
	},
};

export const mergedCompositionAutoGroupColumnDef: ColDef<MergedCompositionRow> = {
	headerName: "",
	pinned: "left",
	minWidth: 240,
	cellClass: "composition-group-col group",
	headerComponent: LabAutoGroupHeader,
	// Without a valueGetter the tree auto-column's value is the path key (the TICKER), while the
	// renderer paints the instrument NAME — so copy/export/sort would use the ticker. Align the
	// value with what the user sees; filler nodes (no data) keep the path key.
	valueGetter: (p) => p.data?.label ?? p.node?.key ?? "",
	cellRendererParams: {
		suppressCount: true,
		innerRenderer: LabGroupCellRenderer,
	},
	suppressHeaderMenuButton: true,
	suppressHeaderContextMenu: true,
	lockPinned: true,
	initialPinned: "left",
};

export type MergedColumnContext = {
	/**
	 * Live weight-precision atom: read via `readAtom` at call time by the weight column's
	 * `valueFormatter` (copy/export path). The DISPLAYED value subscribes on its own instead —
	 * `LabWeightCellRenderer` — so a precision change repaints cells without any `refreshCells`.
	 */
	decimals$: RwAtom<number>;
	onReset: (uuid: string) => void;
	onRemove: (uuid: string) => void;
	onAddPortfolio: (uuid: string) => void;
	onAddInstrument: (uuid: string) => void;
	onUpdateComposition: (uuid: string) => void;
	/**
	 * Live investments atom for the editability predicates (`editability.ts`) and the header
	 * menu's saving lock: column defs only rebuild on investment add/remove, so anything captured
	 * here at build time (AUM, saving status) would go stale — the callbacks read the atom at
	 * call time instead.
	 */
	investments$: RwAtom<PortfolioLabInvestmentItem[]>;
};

export function createMergedColumnDefs(investments: PortfolioLabInvestmentItem[]): ColGroupDef<MergedCompositionRow>[] {
	return investments.map((investment, index): ColGroupDef<MergedCompositionRow> => {
		const { virtualUuid } = investment;
		const color = generateColumnColor(index);
		const bgColor = Color(color).alpha(0.01).toString();
		const leftBorder = `2px solid ${color}`;

		type CellParams = { data?: MergedCompositionRow; context?: MergedColumnContext };
		const liveInvestments = (p: CellParams): PortfolioLabInvestmentItem[] =>
			p.context ? readAtom(p.context.investments$) : [];
		const isEditable = (p: CellParams): boolean =>
			isWeightCellEditable({ virtualUuid, row: p.data, investments: liveInvestments(p) });
		const isMonetaryEditable = (p: CellParams): boolean =>
			isMonetaryCellEditable({ virtualUuid, row: p.data, investments: liveInvestments(p) });

		const deltaCol: ColDef<MergedCompositionRow> = {
			colId: `${virtualUuid}_delta`,
			headerName: "Δ",
			editable: false,
			suppressHeaderMenuButton: true,
			suppressHeaderContextMenu: true,
			// initialWidth (not width): defs are a controlled prop now, so a declared `width`
			// would clobber the user's live resize on every add/remove rebuild.
			initialWidth: 100,
			headerStyle: { backgroundColor: bgColor, borderLeft: leftBorder },
			valueGetter: (p) => p.data?.data[virtualUuid]?.delta ?? undefined,
			valueFormatter: (p: ValueFormatterParams<MergedCompositionRow>) => {
				const delta = p.value as number | undefined;
				if (delta == null || Number.isNaN(delta) || Math.round(Math.abs(delta) * 100) === 0) {
					return "";
				}
				const sign = delta > 0 ? "+" : "−";
				return `${sign}${formatNumber(Math.abs(delta), 2)}%`;
			},
			cellStyle: (p: CellClassParams<MergedCompositionRow>) => {
				const delta = p.value as number | undefined;
				const base = { backgroundColor: bgColor, borderLeft: leftBorder };
				if (delta == null || Number.isNaN(delta) || Math.round(Math.abs(delta) * 100) === 0) {
					return base;
				}
				return {
					...base,
					color: delta > 0 ? themeCSSVars.palette_P600 : themeCSSVars.palette_D500,
					fontWeight: 400,
					fontSize: 11,
				};
			},
			cellRendererSelector: (p) => {
				if (p.node.rowPinned === "bottom") {
					return {
						component: LabNormalizeAndAumCellRenderer,
						params: { virtualUuid },
					};
				}
				return { component: LabDeltaCellRenderer };
			},
		};

		const weightCol: ColDef<MergedCompositionRow> = {
			colId: `${virtualUuid}_weight`,
			headerName: "%",
			suppressHeaderMenuButton: true,
			suppressHeaderContextMenu: true,
			cellDataType: "percentage",
			cellEditor: "agNumberCellEditor",
			// Only `min: 0` — negative weights are not a supported lab concept, and a negative
			// effective weight is an infeasible input the normalize path cannot resolve (it would
			// need a compensating leg > 100 %). `max`/`step`/`precision` stay off: the old hardcoded
			// `precision` fought the AUM-derived dynamic weight grid (MDOT-24199), which is why the
			// whole param bag was disabled — re-adding only the clamp avoids reintroducing that.
			cellEditorParams: { min: 0 },
			editable: isEditable,
			headerStyle: { backgroundColor: bgColor },
			valueGetter: (p) => p.data?.data[virtualUuid]?.weight ?? undefined,
			// No-mutation setter (T3): accept/reject the edit only — the row object stays untouched.
			// `onCellValueChanged` receives the typed value via `event.newValue` (G1 spike, R2) and the
			// grid re-renders from the derived rowData once the atom write lands, so a rejected edit
			// can never leave a phantom value behind.
			valueSetter: (p) => p.newValue != null && !Number.isNaN(p.newValue as number),
			// Copy/export formatting only — the displayed value is owned by LabWeightCellRenderer,
			// which subscribes to `decimals$` itself (no refreshCells on precision change).
			valueFormatter: (p: ValueFormatterParams<MergedCompositionRow, any, MergedColumnContext>) => {
				if (p.value == null || Number.isNaN(p.value as number)) {
					return "N/A";
				}
				return `${formatNumber(p.value as number, readAtom(p.context.decimals$))}%`;
			},
			cellRendererSelector: (p) => {
				if (p.node.rowPinned === "bottom") {
					return { component: LabTotalsWeightCellRenderer, params: { uuid: virtualUuid } };
				}
				// Self-subscribing display cell: repaints on `decimals$`/save-status changes and
				// owns the not-editable dimming (formerly an imperative-refresh cellClassRule).
				return { component: LabWeightCellRenderer, params: { virtualUuid } };
			},
			cellStyle: { backgroundColor: bgColor },
			initialWidth: 100,
		};

		const monetaryCol: ColDef<MergedCompositionRow> = {
			colId: `${virtualUuid}_monetaryWeight`,
			headerName: currencyCode(investment.currency),
			suppressHeaderMenuButton: true,
			suppressHeaderContextMenu: true,
			cellDataType: "floating",
			editable: isMonetaryEditable,
			// Only surface the message on cells that would otherwise be editable — i.e. the
			// sole reason they are locked is the missing AUM, not the row type / read-only state.
			tooltipValueGetter: (p) => {
				const liveInvestment = liveInvestments(p).find((i) => i.virtualUuid === virtualUuid);
				const missingAum = liveInvestment != null && !hasEditableAum(liveInvestment);
				return missingAum && isEditable(p) ? MONETARY_AUM_ZERO_MESSAGE : undefined;
			},
			tooltipComponent: MonetaryAumTooltip,
			headerStyle: { backgroundColor: bgColor },
			valueGetter: (p) => p.data?.data[virtualUuid]?.monetaryWeight ?? undefined,
			// No-mutation setter (T3) — see the weight column's valueSetter comment.
			valueSetter: (p) => p.newValue != null && !Number.isNaN(p.newValue as number),
			valueFormatter: (p: ValueFormatterParams<MergedCompositionRow>) => {
				if (p.value == null || Number.isNaN(p.value as number)) {
					return "N/A";
				}
				return `${formatNumber(p.value as number, 2)}`;
			},
			// `min: 0` mirrors the weight column: a negative monetary edit resolves to a negative
			// effective weight (resolve.ts scales monetary → weight), so without this it would be a
			// backdoor around the weight column's guard. Negative weights are not a supported concept.
			cellEditorParams: { min: 0, precision: 2 },
			cellRendererSelector: (p) => {
				if (p.node.rowPinned === "bottom") {
					return {
						component: LabTotalsMonetaryCellRenderer,
						params: { uuid: virtualUuid, currency: investment.currency },
					};
				}
				// Self-subscribing display cell — see the weight column's selector comment.
				return { component: LabMonetaryCellRenderer, params: { virtualUuid } };
			},
			cellStyle: { backgroundColor: bgColor },
			initialWidth: 100,
			cellClassRules: {
				"!p-0": (p) => p.node.rowPinned === "bottom",
			},
		};

		return {
			groupId: virtualUuid,
			headerName: investment.name,
			marryChildren: true,
			headerGroupComponent: LabPortfolioGroupHeader,
			headerGroupComponentParams: { virtualUuid, color },
			headerStyle: { backgroundColor: bgColor },
			mainMenuItems: (menuParams) => {
				const { onReset, onRemove, onAddPortfolio, onAddInstrument, onUpdateComposition, investments$ } =
					menuParams.context as MergedColumnContext;
				// Menus are built lazily on open — read the saving lock live, like the editability predicates.
				const liveInvestment = readAtom(investments$).find((i) => i.virtualUuid === virtualUuid);
				const isSaving = liveInvestment?.compositionSaveStatus === "saving";
				// Live status gate: disabled while the real investment is transient (SSE-tracked) or a
				// previous commit is still settling. Click-time preconditions stay the final gate.
				const canUpdateComposition = liveInvestment != null && canTriggerUpdateComposition(liveInvestment);
				return [
					{
						name: "Add",
						icon: renderToStaticMarkup(<Icon icon="Plus-circle" size={16} />),
						disabled: isSaving,
						subMenu: [
							{
								name: "Add instrument",
								action: () => onAddInstrument(virtualUuid),
								disabled: isSaving,
								cssClasses: ["[&_.ag-menu-option-icon]:hidden!"],
							},
							{
								name: "Add portfolio",
								action: () => onAddPortfolio(virtualUuid),
								disabled: isSaving,
								cssClasses: ["[&_.ag-menu-option-icon]:hidden!"],
							},
						],
					},
					{
						name: "Reset",
						action: () => onReset(virtualUuid),
						icon: renderToStaticMarkup(<Icon icon="Reload" size={16} />),
						disabled: isSaving,
					},
					"separator",
					{
						name: "Update composition",
						action: () => onUpdateComposition(virtualUuid),
						icon: renderToStaticMarkup(<Icon icon="Portfolio-update" size={16} />),
						disabled: !canUpdateComposition,
					},
					"separator",
					{
						name: "Remove",
						action: () => onRemove(virtualUuid),
						icon: renderToStaticMarkup(<Icon icon="Delete" size={16} />),
						disabled: isSaving,
					},
				];
			},
			children: [deltaCol, weightCol, monetaryCol],
		};
	});
}
